import { useFormikContext } from 'formik';
import { Button, Heading, Text } from '@insight-health-ai/roseate/components';
import { Check, XClose } from '@insight-health-ai/roseate/icons';
import classNames from 'classnames';
import type {
  FormBlock,
  FormField as FormFieldType,
  FormFieldValue,
  FormValues,
} from '../types';

type FieldSectionBlock = FormBlock & {
  title?: string;
  description?: string;
  fields: FormFieldType[];
  columns?: number;
  editable?: boolean;
  titleClassName?: string;
  gridClassName?: string;
};
import { useEditingContext } from '../FormProvider';
import { fieldRegistry } from '../registry/fields';
import { Wrapper } from '../fields/Wrapper';

function formatValue(field: FormFieldType, value: FormFieldValue): string {
  if (value == null || value === '')
    return (field.emptyDisplay as string) ?? '\u2014';
  if (field.type === 'select' || field.type === 'radio') {
    const options = field.options as
      | { label: string; value: string }[]
      | undefined;
    const opt = options?.find((o) => o.value === String(value));
    return opt?.label ?? String(value);
  }
  if (field.type === 'checkbox') {
    return value
      ? ((field.trueLabel as string) ?? 'Yes')
      : ((field.falseLabel as string) ?? 'No');
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function ReviewField({ field }: { field: FormFieldType }) {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FormValues>();
  const {
    editingField,
    editValue,
    startEdit,
    saveEdit,
    cancelEdit,
    setEditValue,
  } = useEditingContext();

  const value = values[field.name] ?? null;
  const isEditing = editingField === field.name;
  const widthClass = field.width === 'full' ? 'col-span-full' : 'col-span-1';
  const error = touched[field.name]
    ? (errors[field.name] as string | undefined)
    : undefined;

  const handleStartEdit = () => {
    if (field.disabled) return;
    startEdit(field.name);
    setEditValue(value);
  };

  const handleSave = () => {
    setFieldValue(field.name, editValue);
    saveEdit();
  };

  if (isEditing) {
    const Input = fieldRegistry[field.type] ?? fieldRegistry.text;
    const isCheckbox = field.type === 'checkbox';
    return (
      <div className={classNames(widthClass, field.className)}>
        {!isCheckbox && (
          <label
            className={classNames(
              'text-base font-semibold text-gray-700',
              field.labelClassName
            )}
          >
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </label>
        )}
        <div
          className={classNames(
            !isCheckbox && 'mt-1',
            'flex items-start gap-2'
          )}
        >
          <div className="flex-1">
            <Input
              field={field}
              value={editValue}
              onChange={setEditValue}
              autoFocus
            />
          </div>
          <Button size="sm" icon={<Check />} compact onClick={handleSave} />
          <Button
            size="sm"
            variant="text"
            appearance="secondary"
            icon={<XClose />}
            compact
            onClick={cancelEdit}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        widthClass,
        !field.disabled && 'cursor-pointer group',
        field.className
      )}
      onClick={handleStartEdit}
      role={field.disabled ? undefined : 'button'}
      tabIndex={field.disabled ? undefined : 0}
      onKeyDown={
        field.disabled
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStartEdit();
              }
            }
      }
    >
      <label
        className={classNames(
          'text-base font-semibold',
          field.disabled ? 'text-gray-400' : 'text-gray-700',
          field.labelClassName
        )}
      >
        {field.label}
        {field.required && !field.disabled && (
          <span className="text-red-500 ml-0.5">*</span>
        )}
      </label>
      <div
        className={classNames(
          'mt-1 rounded-lg border border-transparent px-4 py-3 text-base transition-colors',
          field.disabled
            ? 'text-gray-400'
            : 'text-gray-900 group-hover:border-gray-300',
          error && 'border-red-300',
          field.inputClassName
        )}
      >
        {formatValue(field, value)}
      </div>
      {error && (
        <Text appearance="danger" className="mt-1 text-sm">
          {error}
        </Text>
      )}
    </div>
  );
}

function EditField({ field }: { field: FormFieldType }) {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<FormValues>();
  const value = values[field.name] ?? null;
  const error = touched[field.name]
    ? (errors[field.name] as string | undefined)
    : undefined;

  const Input = fieldRegistry[field.type] ?? fieldRegistry.text;

  return (
    <Wrapper field={field} error={error}>
      <Input
        field={field}
        value={value}
        onChange={(v) => setFieldValue(field.name, v)}
      />
    </Wrapper>
  );
}

export function FormField({ block }: { block: FieldSectionBlock }) {
  const columns = block.columns ?? 1;
  const gridClass =
    columns === 2
      ? 'grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5'
      : 'flex flex-col gap-5';

  return (
    <div className={block.className}>
      {block.title && (
        <div className="border-b border-gray-200 pb-2 mb-5">
          <Heading level={3} className={block.titleClassName}>
            {block.title}
          </Heading>
          {block.description && (
            <Text appearance="secondary">{block.description}</Text>
          )}
        </div>
      )}
      <div className={classNames(gridClass, block.gridClassName)}>
        {block.fields.map((field) =>
          block.editable ? (
            <ReviewField key={field.name} field={field} />
          ) : (
            <EditField key={field.name} field={field} />
          )
        )}
      </div>
    </div>
  );
}
